import { Button, Field, Form, RawInput } from '@payaca/ambrosia';
import { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { actions as appActions } from '@/api/app';

import CollapsiblePanel, {
  CollapsiblePanelStyleVariant,
} from '@payaca/components/collapsiblePanel/CollapsiblePanel';

import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import { getFormServiceError } from '@/helpers/formHelper';

import PasswordValidationFeedback from '../passwordValidationFeedback/PasswordValidationFeedback';

type Props = {
  resetToken: string;
  onSuccessCallback?: () => void;
};

const schema = z
  .object({
    newPassword: z
      .string()
      .min(12, { message: 'Password must be at least 12 characters' })
      .regex(/[A-Za-z]/, { message: 'Password must contain a letter' })
      .regex(/[0-9]/, { message: 'Password must contain a number' }),

    confirmNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    path: ['confirmNewPassword'],
    message: 'The passwords entered do not match',
  });

type FormValues = z.infer<typeof schema>;

const ResetPasswordForm: FunctionComponent<Props> = ({
  resetToken,
  onSuccessCallback,
}) => {
  const dispatch = useDispatch();

  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordErrorMessage, setResetPasswordErrorMessage] =
    useState<string>();
  const [showPasswordValidationFeedback, setShowPasswordValidationFeedback] =
    useState(false);

  const formMethods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
    mode: 'onChange',
  });

  const { control, watch } = formMethods;

  const newPasswordValue = watch('newPassword');

  const onSubmit = useCallback(
    (values: FormValues) => {
      setIsResettingPassword(true);
      setResetPasswordErrorMessage(undefined);

      const payload = {
        password: values.newPassword,
      };

      dispatch(
        appActions.resetPassword(resetToken, payload, (error: any) => {
          if (!error) {
            onSuccessCallback?.();
          } else {
            setResetPasswordErrorMessage(
              getFormServiceError('resetPassword', error)
            );
          }
          setIsResettingPassword(false);
        })
      );
    },
    [dispatch, resetToken, onSuccessCallback]
  );

  return (
    <div className="reset-password-form-container">
      <Form methods={formMethods} onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          <Controller
            name="newPassword"
            control={control}
            render={({ field }) => (
              <Field name={field.name}>
                <Field.Label>New password</Field.Label>
                <RawInput
                  {...field}
                  type="password"
                  placeholder="New password"
                  onFocus={() => setShowPasswordValidationFeedback(true)}
                />
              </Field>
            )}
          />

          <Controller
            name="confirmNewPassword"
            control={control}
            render={({ field }) => (
              <Field name={field.name}>
                <Field.Label>Confirm password</Field.Label>
                <RawInput
                  {...field}
                  type="password"
                  placeholder="Confirm password"
                />
              </Field>
            )}
          />
        </div>

        <div className="my-4">
          <CollapsiblePanel
            isOpen={showPasswordValidationFeedback}
            styleVariant={CollapsiblePanelStyleVariant.UNSTYLED}
            shouldRenderHeader={false}
          >
            <PasswordValidationFeedback password={newPasswordValue} />
          </CollapsiblePanel>
        </div>

        <div className="button-wrapper">
          <Button type="submit" isProcessing={isResettingPassword}>
            Reset password
          </Button>
        </div>

        {resetPasswordErrorMessage && (
          <ErrorMessage message={resetPasswordErrorMessage} />
        )}
      </Form>
    </div>
  );
};

export default ResetPasswordForm;
